import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CalendarRange, Flame, Loader2, Trophy } from "lucide-react";
import { getRankings } from "@/lib/board.functions";
import { formatIDR } from "@/lib/board-constants";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal } from "@/components/motion-kit";

type Row = { slug: string; name: string; category: string; amount: number; clicks: number };

function RankList({ rows, loading }: { rows: Row[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label="Memuat peringkat">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">Belum ada bid pada periode ini.</p>;
  }
  return (
    <ol className="space-y-2">
      {rows.map((r, i) => (
        <li key={r.slug}>
          <Card className="transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
            <Link
              to="/produk/$slug"
              params={{ slug: r.slug }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
            >
              <span
                className={`grid size-7 shrink-0 place-items-center rounded-md text-xs font-semibold tabular-nums ${
                  i === 0 ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
                }`}
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{r.name}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {r.category} · {r.clicks} klik
                </span>
              </span>
              <Badge variant="secondary" className="shrink-0 tabular-nums">
                {formatIDR(r.amount)}
              </Badge>
            </Link>
          </Card>
        </li>
      ))}
    </ol>
  );
}

function formatUpdatedAt(ts: number): string {
  if (!ts) return "Belum diperbarui";
  const date = new Date(ts);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("id-ID", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `Diperbarui: ${day} ${month} ${year}, ${hours}.${minutes} WIB`;
}

/** Section peringkat: hari ini di atas, mingguan di bawahnya. */
export function RankingSections() {
  const fn = useServerFn(getRankings);
  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["rankings"],
    queryFn: () => fn(),
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: true,
  });

  const loading = isLoading || isFetching;

  return (
    <section className="py-2">
      <div className="flex items-center gap-2">
        <Trophy className="size-4 text-primary" />
        <h2 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">Papan peringkat berkala</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Siapa yang paling agresif menaikkan bid hari ini dan sepanjang minggu ini.
      </p>

      <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
        {isFetching && !isLoading ? (
          <>
            <Loader2 className="size-3 animate-spin" />
            <span>Memperbarui peringkat…</span>
          </>
        ) : (
          <>
            <span className="size-1.5 rounded-full bg-emerald-500" />
            <span>{formatUpdatedAt(dataUpdatedAt)}</span>
          </>
        )}
      </div>

      <div className="mt-4 space-y-6">
        <Reveal>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Flame className="size-4 text-primary" /> Peringkat teratas hari ini
          </h3>
          <RankList rows={data?.today ?? []} loading={loading} />
        </Reveal>
        <Reveal delay={0.06}>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <CalendarRange className="size-4 text-primary" /> Peringkat minggu ini
          </h3>
          <RankList rows={data?.week ?? []} loading={loading} />
        </Reveal>
      </div>
    </section>
  );
}
